import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { ApiService } from '../../core/api.service';
import { SocketService } from '../../core/socket.service';
import { displayValue, statusBadgeClass, statusBadgeIcon } from '../../shared/status-display';

interface ChartSlice {
  label: string;
  value: number;
  color: string;
}

interface TrendPoint {
  date: string;
  produced: number;
  rejected: number;
  entries: number;
}

interface AnalyticsItem extends ChartSlice {
  icon: string;
  suffix?: string;
}

interface AvailabilityItem extends AnalyticsItem {
  width: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [FormsModule, NgFor, NgIf, NgClass, DecimalPipe, MatDatepickerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatNativeDateModule, MatSelectModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  summary: any;
  loading = false;
  datePreset = 'today';
  rangeStart: Date | null = new Date();
  rangeEnd: Date | null = new Date();
  private appliedStart = this.toDateInput(this.rangeStart);
  private appliedEnd = this.toDateInput(this.rangeEnd);

  cards = [
    { key: 'workers', label: 'Active Workers', icon: 'groups', tone: 'teal' },
    { key: 'activeAssignments', label: 'Live Assignments', icon: 'assignment', tone: 'blue' },
    { key: 'pendingApprovals', label: 'Need Review', icon: 'rate_review', tone: 'amber' },
    { key: 'lockedOperations', label: 'Ready Operations', icon: 'verified', tone: 'green' }
  ];

  private palette = ['#0f766e', '#2563eb', '#b45309', '#7c3aed', '#dc2626', '#475569', '#15803d'];

  constructor(private api: ApiService, private socket: SocketService) {}

  /**
   * Loads dashboard data and subscribes to realtime events that affect KPIs.
   * @returns void.
   */
  ngOnInit() {
    this.load();
    this.socket.on('attendance:event-imported').subscribe(() => this.load());
    this.socket.on('attendance:updated').subscribe(() => this.load());
    this.socket.on('production:submitted').subscribe(() => this.load());
    this.socket.on('assignment:updated').subscribe(() => this.load());
  }

  /**
   * Fetches the dashboard summary from the backend.
   * @returns void.
   */
  load() {
    this.loading = true;
    this.api.get(`/dashboard/summary${this.dateQuery()}`).subscribe({
      next: (summary) => {
        this.summary = summary;
        this.loading = false;
      },
      error: () => {
        this.summary = {};
        this.loading = false;
      }
    });
  }

  /** Keeps start-date edits local until the user completes the selected range. */
  onRangeStartChange() {}

  /** Applies the selected dashboard date preset and reloads summary data. */
  applyPreset(preset: string) {
    this.datePreset = preset;
    if (preset === 'custom') return;

    const { start, end } = this.presetRange(preset);
    this.rangeStart = start;
    this.rangeEnd = end;
    this.appliedStart = this.toDateInput(start);
    this.appliedEnd = this.toDateInput(end);
    this.load();
  }

  /** Applies the date range only after both dates are available and valid. */
  applyDateRange() {
    if (this.datePreset !== 'custom') return;
    if (!this.rangeStart || !this.rangeEnd) return;

    if (this.rangeStart > this.rangeEnd) {
      [this.rangeStart, this.rangeEnd] = [this.rangeEnd, this.rangeStart];
    }

    const nextStart = this.toDateInput(this.rangeStart);
    const nextEnd = this.toDateInput(this.rangeEnd);
    if (nextStart === this.appliedStart && nextEnd === this.appliedEnd) return;

    this.appliedStart = nextStart;
    this.appliedEnd = nextEnd;
    this.load();
  }

  /**
   * Converts grouped count objects into chart slices with stable colors.
   * @param group Counts keyed by label.
   * @returns Chart slices used by the dashboard template.
   */
  chartData(group: Record<string, number> | undefined): ChartSlice[] {
    return Object.entries(group || {}).map(([label, value], index) => ({
      label,
      value: Number(value || 0),
      color: this.palette[index % this.palette.length]
    }));
  }

  /**
   * Calculates the total value for a chart slice collection.
   * @param items Chart slices.
   * @returns Sum of slice values.
   */
  total(items: ChartSlice[]) {
    return items.reduce((sum, item) => sum + item.value, 0);
  }

  /**
   * Calculates a whole-number percentage while protecting against divide-by-zero.
   * @param value Numerator.
   * @param total Denominator.
   * @returns Percentage from 0 to 100.
   */
  percent(value: number, total: number) {
    return total ? Math.round((value / total) * 100) : 0;
  }

  /** Returns production trend rows from the loaded summary. */
  trend(): TrendPoint[] {
    return this.summary?.productionTrend || [];
  }

  /** Calculates the maximum trend total for relative bar scaling. */
  trendMax() {
    return Math.max(...this.trend().map((row) => row.produced + row.rejected), 1);
  }

  /**
   * Calculates the production bar height for a trend point.
   * @param row Trend row.
   * @returns Percentage height used by the chart.
   */
  barHeight(row: TrendPoint) {
    return Math.max(((row.produced + row.rejected) / this.trendMax()) * 100, row.entries ? 8 : 0);
  }

  /**
   * Calculates rejected quantity height inside a production trend bar.
   * @param row Trend row.
   * @returns Percentage rejected segment height.
   */
  rejectedHeight(row: TrendPoint) {
    const total = row.produced + row.rejected;
    return total ? (row.rejected / total) * 100 : 0;
  }

  /** Returns completed assignment percentage across active plus completed work. */
  completionRate() {
    const completed = Number(this.summary?.completedAssignments || 0);
    const active = Number(this.summary?.activeAssignments || 0);
    return this.percent(completed, completed + active);
  }

  /** Returns produced percentage across produced plus rejected totals. */
  qualityRate() {
    const produced = Number(this.summary?.productionTotals?.produced || 0);
    const rejected = Number(this.summary?.productionTotals?.rejected || 0);
    return this.percent(produced, produced + rejected);
  }

  /** Returns a display-only summary derived from the existing attendance status counts. */
  attendanceAnalytics(): AnalyticsItem[] {
    const counts = this.summary?.filteredAttendanceStatus || {};
    const present = this.sumMatching(counts, ['Checked In', 'In Progress', 'Awaiting Action', 'On Break', 'Break Completed', 'Pending Review']);
    const completed = this.sumMatching(counts, ['Completed', 'Approved']);
    const absent = Math.max(Number(this.summary?.workers || 0) - present - completed, 0);
    const late = this.sumMatching(counts, ['Late']);

    return [
      { label: 'Present Employees', value: present, icon: 'badge', color: '#0f766e' },
      { label: 'Absent Employees', value: absent, icon: 'person_off', color: '#dc2626' },
      { label: 'Late Check-ins', value: late, icon: 'schedule', color: '#b45309' },
      { label: 'Shift Completion Percentage', value: this.shiftCompletionRate(), suffix: '%', icon: 'task_alt', color: '#2563eb' }
    ];
  }

  /** Returns compact chart rows for the attendance analytics card. */
  workforceAvailability(): AvailabilityItem[] {
    const items = this.attendanceAnalytics();
    const max = Math.max(...items.map((item) => item.value), 1);
    return items.map((item) => ({ ...item, width: item.suffix ? item.value : Math.max((item.value / max) * 100, item.value ? 8 : 0) }));
  }

  /**
   * Formats a timestamp for recent activity lists.
   * @param value Date string.
   * @returns Localized date/time label.
   */
  formatDate(value: string) {
    return value ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : '--';
  }

  /**
   * Formats a trend date as a short day/month label.
   * @param value Date string.
   * @returns Localized day label.
   */
  formatDay(value: string) {
    return value ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(new Date(value)) : '--';
  }

  displayCell(value: any) {
    return displayValue(value);
  }

  statusClass(value: any) {
    return statusBadgeClass(value);
  }

  statusIcon(value: any) {
    return statusBadgeIcon(value);
  }

  /** Formats the current date-aware range for chart subtitles. */
  rangeLabel() {
    return this.appliedStart && this.appliedEnd ? `${this.formatDay(this.appliedStart)} - ${this.formatDay(this.appliedEnd)}` : 'Selected period';
  }

  private shiftCompletionRate() {
    const counts = this.summary?.filteredAssignmentStatus || {};
    const completed = Number(counts.Completed || 0);
    const active = Number(counts.Scheduled || 0) + Number(counts['In Progress'] || 0) + Number(counts['Pending Review'] || 0);
    return this.percent(completed, completed + active);
  }

  private dateQuery() {
    if (!this.appliedStart || !this.appliedEnd) return '';
    const params = new URLSearchParams({ startDate: this.appliedStart, endDate: this.appliedEnd });
    return `?${params.toString()}`;
  }

  private presetRange(preset: string) {
    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);

    if (preset === 'thisWeek') {
      const day = start.getDay() || 7;
      start.setDate(start.getDate() - day + 1);
    }

    if (preset === 'lastWeek') {
      const day = start.getDay() || 7;
      start.setDate(start.getDate() - day - 6);
      end.setTime(start.getTime());
      end.setDate(start.getDate() + 6);
    }

    if (preset === 'thisMonth') {
      start.setDate(1);
    }

    if (preset === 'lastMonth') {
      start.setDate(1);
      start.setMonth(start.getMonth() - 1);
      end.setDate(0);
    }

    return { start, end };
  }

  private toDateInput(value: Date | null) {
    if (!value) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private sumMatching(counts: Record<string, number>, labels: string[]) {
    return Object.entries(counts).reduce((sum, [label, value]) => {
      return labels.some((target) => label.toLowerCase().includes(target.toLowerCase())) ? sum + Number(value || 0) : sum;
    }, 0);
  }

  /** Tracks chart slices by label for efficient Angular rendering. */
  trackByLabel(_: number, item: ChartSlice) {
    return item.label;
  }

  /** Tracks trend rows by date for efficient Angular rendering. */
  trackByDate(_: number, item: TrendPoint) {
    return item.date;
  }
}
